import { Link, useNavigate } from 'react-router-dom'
import { styled } from '../../../lib/stitches'
import Button from '../../core/Button/Button'
import {
  ArpeggiLogoLight,
  DiscordLight,
  InstagramLight,
  MirrorLight,
  TwitterLight
} from '../../core/icons/icons'
import Text from '../../core/Text/Text'
import { PUBLIC_NOTION_URL, SOCIAL_MEDIA_LINKS } from '../constants'

const segmentMarketingPage = (button: string) => {
  window.analytics.track('Button Clicked', {
    button: button,
    location: 'marketing page',
    source: window.location.pathname
  })
}
const FooterContent = styled('div', {
  maxWidth: '375px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  padding: '30px 24px',
  color: '$gray01',

  '@tablet': {
    maxWidth: '1440px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '50px 98px 95px'
  },

  '> div': {
    flexGrow: 1
  }
})

const Logo = styled('div', {
  pointer: 'cursor',
  display: 'flex',
  alignItems: 'center'
})

const SocialMedia = styled('div', {
  display: 'flex',
  '> a': {
    marginRight: '20px'
  },
  marginRight: '20px',
  padding: '37px 0 27px',

  path: {
    fill: '#707070'
  },

  '@tablet': {
    marginTop: 0
  }
})

const Actions = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  padding: '48px 0 0',
  margin: '0 auto',
  width: '100%',
  '> button': {
    marginBottom: '8px'
  },

  '@tablet': {
    flexDirection: 'row-reverse',
    border: 'none',
    padding: 0,
    margin: 0,
    maxWidth: 'none',
    width: 'auto',
    '> button': {
      marginRight: '16px'
    }
  }
})

const FooterWrapper = styled('div', {
  width: '100%',
  backgroundColor: '$black'
})

const Email = styled('a', {
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '16px',
  color: '#9E9E9E',
  '&:hover': {
    color: '#9E9E9E'
  },
  '@tablet': {
    color: '#626262',
    '&:hover': {
      color: '#626262'
    }
  }
})

const Title = styled('div', {
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '16px',
  color: '#F6F8F9',
  marginBottom: '33px'
})

const ListItem = styled('div', {
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '16px',
  color: '#676767'
})

const Wrapper = styled('div', {
  paddingBottom: '34px',
  borderBottom: '1px solid $gray13',
  '@tablet': {
    borderBottom: 'none'
  }
})

const FooterNav = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginTop: '38px',
  padding: '0 12px',
  '@tablet': {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 0,
    padding: '0 12px'
  }
})

const ListWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  a: {
    textDecoration: 'none',
    marginBottom: '20px'
  }
})

const StyledMirrorLight = styled('a', {
  path: {
    stroke: '#707070',
    fill: 'inherit'
  }
})

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

const Footer = () => {
  const navigate = useNavigate()
  return (
    <FooterWrapper>
      <FooterContent>
        <Wrapper>
          <Logo onClick={scrollToTop}>
            <ArpeggiLogoLight />
            <Text
              css={{
                fontSize: '22.5px',
                lineHeight: '30px',
                marginLeft: '13px',
                fontWeight: '600',
                fontFamily: '$poppins',
                '@tablet': {
                  fontSize: '22px',
                  lineHeight: '26px',
                  letterSpacing: '-0.02em'
                }
              }}
              type="primary_inverted"
            >
              Arpeggi Labs
            </Text>
          </Logo>
          <SocialMedia css={{ display: 'flex', alignItems: 'center' }}>
            <a href={SOCIAL_MEDIA_LINKS.TWITTER} target="_blank">
              <TwitterLight />
            </a>
            <a href={SOCIAL_MEDIA_LINKS.INSTAGRAM} target="_blank">
              <InstagramLight />
            </a>
            <a href={SOCIAL_MEDIA_LINKS.DISCORD} target="_blank">
              <DiscordLight />
            </a>
            <StyledMirrorLight href={SOCIAL_MEDIA_LINKS.MIRROR} target="_blank">
              <MirrorLight />
            </StyledMirrorLight>
          </SocialMedia>
          <Email href="mailto:support@arpeggi.io">hello@arpeggi.io</Email>
        </Wrapper>

        <FooterNav>
          <div>
            <Title>Product</Title>
            <ListWrapper>
              <Link
                to="/studio"
                onClick={() => {
                  segmentMarketingPage('Arpeggi Studio')
                }}
              >
                <ListItem>Arpeggi Studio</ListItem>
              </Link>

              <Link
                to="/explore/sounds"
                onClick={() => {
                  segmentMarketingPage('Explore Sounds')
                }}
              >
                <ListItem>Explore Sounds</ListItem>
              </Link>

              <a href={PUBLIC_NOTION_URL} target="_blank">
                <ListItem css={{ paddingBottom: 0 }}>Learn More</ListItem>
              </a>
            </ListWrapper>
          </div>
          <div>
            <Title>Legal</Title>
            <ListWrapper>
              <Link
                to="/legal/terms-of-service"
                target="_blank"
                onClick={() => {
                  segmentMarketingPage('Terms of Service')
                }}
              >
                <ListItem>Terms of Service</ListItem>
              </Link>
              <Link
                to="/legal/privacy-policy"
                target="_blank"
                onClick={() => {
                  segmentMarketingPage('Privacy')
                }}
              >
                <ListItem>Privacy</ListItem>
              </Link>
            </ListWrapper>
          </div>
        </FooterNav>
        <Actions>
          <Button
            type="white"
            onClick={() => {
              segmentMarketingPage('Enter app')
              navigate('/studio')
            }}
            css={{
              height: '40px',
              borderRadius: '10px',
              padding: '0 16px',
              fontSize: '16px',
              fontWeight: 500
            }}
          >
            Enter app
          </Button>
          <Button
            type="outlined"
            noHover
            onClick={() => {
              segmentMarketingPage('Login')
              navigate('/home')
            }}
            css={{
              borderColor: '$background__secondary',
              color: '$text_inverted__primary',
              height: '40px',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: 500,
              '@tablet': {
                borderColor: 'black'
              }
            }}
          >
            Login
          </Button>
        </Actions>
      </FooterContent>
    </FooterWrapper>
  )
}

export default Footer
